import React, { useState, useEffect } from 'react';
import { CashExpense, CashExpenseItem, CashAccount, ExpenseArticle, Tournament } from '../types';
import { formatDate as formatDateUtil } from '../utils/dateFormat';
import AccountTypeSelector from '../components/AccountTypeSelector';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

const TOURNAMENT_STATUS_PRIORITY: Record<string, number> = {
  ACTIVE: 0,
  PLANNED: 1,
  COMPLETED: 2,
  CANCELLED: 3
};
const TOURNAMENT_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Активные',
  PLANNED: 'Запланированные',
  COMPLETED: 'Завершенные',
  CANCELLED: 'Отмененные'
};

const CashExpenses: React.FC = () => {
  const [expenses, setExpenses] = useState<CashExpense[]>([]);
  const [accounts, setAccounts] = useState<CashAccount[]>([]);
  const [expenseArticles, setExpenseArticles] = useState<ExpenseArticle[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState<CashExpense | null>(null);
  const [formData, setFormData] = useState({
    accountId: '',
    expenseArticleId: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    status: 'DRAFT' as 'DRAFT' | 'POSTED' | 'CANCELLED',
    tournamentId: ''
  });
  const [items, setItems] = useState<Partial<CashExpenseItem>[]>([]);
  const [filters, setFilters] = useState({
    status: '',
    accountId: '',
    expenseArticleId: '',
    from: '',
    to: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [selectedExpenseJson, setSelectedExpenseJson] = useState<string>('');

  useEffect(() => {
    fetchData();
  }, [pagination.page, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Загружаем справочники
      const [accountsData, articlesData, tournamentsData] = await Promise.all([
        apiGet<CashAccount[]>('/cash-accounts'),
        apiGet<ExpenseArticle[]>('/expense-articles?type=EXPENSE&isActive=true'),
        apiGet<{ tournaments?: Tournament[] } | Tournament[]>('/tournaments?page=1&limit=1000')
      ]);
      
      setAccounts(accountsData);
      setExpenseArticles(articlesData);
      const tournaments = Array.isArray(tournamentsData) ? tournamentsData : (tournamentsData.tournaments || []);
      const sortedTournaments = [...tournaments].sort((a, b) => {
        const statusA = String(a.status || '').toUpperCase();
        const statusB = String(b.status || '').toUpperCase();
        const priorityA = TOURNAMENT_STATUS_PRIORITY[statusA] ?? Number.MAX_SAFE_INTEGER;
        const priorityB = TOURNAMENT_STATUS_PRIORITY[statusB] ?? Number.MAX_SAFE_INTEGER;
        if (priorityA !== priorityB) return priorityA - priorityB;
        const endTsA = a.endDate ? new Date(a.endDate).getTime() : 0;
        const endTsB = b.endDate ? new Date(b.endDate).getTime() : 0;
        if (endTsA !== endTsB) return endTsB - endTsA;
        return String(a.name || '').localeCompare(String(b.name || ''), 'ru');
      });
      setTournaments(sortedTournaments);

      // Загружаем расходы
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });
      
      const data = await apiGet<{ expenses?: CashExpense[]; pagination?: any }>(`/cash-expenses?${params.toString()}`);
      setExpenses(data.expenses || []);
      if (data.pagination) {
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const submitData: any = {
        accountId: formData.accountId,
        description: formData.description || '',
        date: formData.date,
        status: formData.status
      };

      // Если есть табличная часть, используем ее
      if (items.length > 0) {
        submitData.items = items.map(item => ({
          expenseArticleId: item.expenseArticleId,
          tournamentId: item.tournamentId || null,
          amount: Number(item.amount),
          comment: item.comment || null
        }));
      } else {
        // Старый формат (для обратной совместимости)
        submitData.expenseArticleId = formData.expenseArticleId;
        submitData.amount = Number(formData.amount);
        submitData.tournamentId = formData.tournamentId || null;
      }

      if (editingExpense) {
        await apiPut(`/cash-expenses/${editingExpense.id}`, submitData);
      } else {
        await apiPost('/cash-expenses', submitData);
      }

      await fetchData();
      handleCloseForm();
    } catch (error: any) {
      console.error('Error saving expense:', error);
      alert(error.message || 'Ошибка при сохранении расхода');
    }
  };

  const handleEdit = (expense: CashExpense) => {
    setEditingExpense(expense);
    setFormData({
      accountId: expense.accountId,
      expenseArticleId: expense.expenseArticleId || '',
      amount: expense.amount?.toString() || '',
      description: expense.description,
      date: expense.date.split('T')[0],
      status: expense.status,
      tournamentId: expense.tournamentId || ''
    });
    // Если есть табличная часть, используем ее
    if (expense.items && expense.items.length > 0) {
      setItems(expense.items.map(item => ({
        id: item.id,
        expenseArticleId: item.expenseArticleId,
        tournamentId: item.tournamentId || '',
        amount: item.amount,
        comment: item.comment || ''
      })));
    } else {
      setItems([]);
    }
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить этот расход?')) return;
    
    try {
      await apiDelete(`/cash-expenses/${id}`);
      await fetchData();
    } catch (error: any) {
      console.error('Error deleting expense:', error);
      alert(error.message || 'Ошибка при удалении расхода');
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await apiPut(`/cash-expenses/${id}`, { status: newStatus });
      await fetchData();
    } catch (error: any) {
      console.error('Error changing status:', error);
      alert(error.message || 'Ошибка при изменении статуса');
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingExpense(null);
    setFormData({
      accountId: '',
      expenseArticleId: '',
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      status: 'DRAFT',
      tournamentId: ''
    });
    setItems([]);
  };

  const addItem = () => {
    setItems([...items, { expenseArticleId: '', tournamentId: '', amount: 0, comment: '' }]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: keyof CashExpenseItem, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const calculateTotalAmount = () => {
    return items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'text-yellow-600';
      case 'POSTED': return 'text-green-600';
      case 'CANCELLED': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'Черновик';
      case 'POSTED': return 'Проведен';
      case 'CANCELLED': return 'Отменен';
      default: return status;
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount) + ' MDL';
  };

  const formatDate = (dateString: string) => {
    return formatDateUtil(dateString);
  };

  const groupedTournaments = tournaments.reduce<Record<string, Tournament[]>>((acc, tournament) => {
    const status = String(tournament.status || '').toUpperCase();
    if (!acc[status]) acc[status] = [];
    acc[status].push(tournament);
    return acc;
  }, {});
  const orderedTournamentStatuses = Object.keys(groupedTournaments).sort((a, b) => {
    const pa = TOURNAMENT_STATUS_PRIORITY[a] ?? Number.MAX_SAFE_INTEGER;
    const pb = TOURNAMENT_STATUS_PRIORITY[b] ?? Number.MAX_SAFE_INTEGER;
    return pa - pb;
  });

  const handleViewJson = (expense: CashExpense) => {
    const jsonData = JSON.stringify(expense, null, 2);
    setSelectedExpenseJson(jsonData);
    setShowJsonModal(true);
  };

  const handleCloseJsonModal = () => {
    setShowJsonModal(false);
    setSelectedExpenseJson('');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Расходы денежных средств</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700"
        >
          Добавить расход
        </button>
      </div>

      {/* Фильтры */}
      <div className="mb-4 grid grid-cols-2 md:grid-cols-5 gap-2">
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        >
          <option value="">Все статусы</option>
          <option value="DRAFT">Черновик</option>
          <option value="POSTED">Проведен</option>
          <option value="CANCELLED">Отменен</option>
        </select>
        <select
          value={filters.accountId}
          onChange={(e) => setFilters({ ...filters, accountId: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        >
          <option value="">Все счета</option>
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.name}
            </option>
          ))}
        </select>
        <select
          value={filters.expenseArticleId}
          onChange={(e) => setFilters({ ...filters, expenseArticleId: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        >
          <option value="">Все статьи</option>
          {expenseArticles.map((article) => (
            <option key={article.id} value={article.id}>
              {article.code} - {article.name}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={filters.from}
          onChange={(e) => setFilters({ ...filters, from: e.target.value })}
          placeholder="От даты"
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        />
        <input
          type="date"
          value={filters.to}
          onChange={(e) => setFilters({ ...filters, to: e.target.value })}
          placeholder="До даты"
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        />
      </div>

      {/* Таблица расходов */}
      <div className="bg-white shadow overflow-hidden rounded-md">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Номер
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Дата
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Статья затрат
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Турнир
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Сумма
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Счет
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Статус
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {expenses.map((expense) => (
              <tr key={expense.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                  {expense.number || '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                  {formatDate(expense.date)}
                </td>
                <td className="px-3 py-2 text-xs text-gray-900 max-w-32 truncate">
                  {expense.expenseArticle?.code} - {expense.expenseArticle?.name}
                </td>
                <td className="px-3 py-2 text-xs text-gray-900 max-w-24 truncate">
                  {expense.tournament?.name || '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                  {formatAmount(expense.amount)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                  {expense.account ? `${expense.account.name} (${expense.account.currency})` : '-'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  <span className={getStatusColor(expense.status)}>
                    {getStatusText(expense.status)}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium">
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleEdit(expense)}
                      className="text-indigo-600 hover:text-indigo-900 text-sm"
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleViewJson(expense)}
                      className="text-blue-600 hover:text-blue-900 text-sm"
                      title="Просмотр JSON"
                    >
                      📄
                    </button>
                    {expense.status === 'DRAFT' && (
                      <button
                        onClick={() => handleStatusChange(expense.id, 'POSTED')}
                        className="text-green-600 hover:text-green-900 text-sm"
                        title="Провести"
                      >
                        ✅
                      </button>
                    )}
                    {expense.status === 'POSTED' && (
                      <button
                        onClick={() => handleStatusChange(expense.id, 'CANCELLED')}
                        className="text-red-600 hover:text-red-900 text-sm"
                        title="Отменить"
                      >
                        ❌
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(expense.id)}
                      className="text-red-600 hover:text-red-900 text-sm"
                      title="Удалить"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Пагинация */}
      {pagination.totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <div className="flex space-x-1">
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              disabled={pagination.page === 1}
              className="px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ←
            </button>
            <span className="px-2 py-1 text-xs text-gray-700">
              {pagination.page}/{pagination.totalPages}
            </span>
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              disabled={pagination.page === pagination.totalPages}
              className="px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              →
            </button>
          </div>
        </div>
      )}

      {/* Модальное окно формы */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-4 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-2">
              <h3 className="text-base font-medium text-gray-900 mb-3">
                {editingExpense ? 'Редактировать расход' : 'Добавить расход'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700">Счет *</label>
                  <AccountTypeSelector
                    value={formData.accountId}
                    onChange={(accountId) => setFormData({ ...formData, accountId })}
                    required={true}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Дата *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    required
                  />
                </div>
                
                {/* Табличная часть */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-xs font-medium text-gray-700">Табличная часть</label>
                    <button
                      type="button"
                      onClick={addItem}
                      className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700"
                    >
                      + Добавить строку
                    </button>
                  </div>
                  {items.length > 0 ? (
                    <div className="border border-gray-300 rounded overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200 text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Статья затрат *</th>
                            <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Турнир</th>
                            <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Сумма *</th>
                            <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Комментарий</th>
                            <th className="px-2 py-1 text-left text-xs font-medium text-gray-500"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {items.map((item, index) => (
                            <tr key={index}>
                              <td className="px-2 py-1">
                                <select
                                  value={item.expenseArticleId || ''}
                                  onChange={(e) => updateItem(index, 'expenseArticleId', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                                  required
                                >
                                  <option value="">Выберите статью</option>
                                  {expenseArticles.map((article) => (
                                    <option key={article.id} value={article.id}>
                                      {article.code} - {article.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-1">
                                <select
                                  value={item.tournamentId || ''}
                                  onChange={(e) => updateItem(index, 'tournamentId', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                                >
                                  <option value="">Выберите турнир</option>
                                  {orderedTournamentStatuses.map((status) => (
                                    <optgroup
                                      key={status}
                                      label={`──────── ${TOURNAMENT_STATUS_LABELS[status] || status} ────────`}
                                    >
                                      {(groupedTournaments[status] || []).map((tournament) => (
                                        <option key={tournament.id} value={tournament.id}>
                                          {tournament.name}
                                        </option>
                                      ))}
                                    </optgroup>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-1">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={item.amount || ''}
                                  onChange={(e) => updateItem(index, 'amount', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                                  required
                                />
                              </td>
                              <td className="px-2 py-1">
                                <input
                                  type="text"
                                  value={item.comment || ''}
                                  onChange={(e) => updateItem(index, 'comment', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                                />
                              </td>
                              <td className="px-2 py-1">
                                <button
                                  type="button"
                                  onClick={() => removeItem(index)}
                                  className="text-red-600 hover:text-red-900 text-xs"
                                >
                                  🗑️
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-50">
                          <tr>
                            <td colSpan={2} className="px-2 py-1 text-xs font-medium text-gray-700 text-right">
                              Итого:
                            </td>
                            <td className="px-2 py-1 text-xs font-medium text-gray-900">
                              {formatAmount(calculateTotalAmount())}
                            </td>
                            <td colSpan={2}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 mb-2">
                      Табличная часть пуста. Нажмите "Добавить строку" для добавления позиций.
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Описание</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Турнир</label>
                  <select
                    value={formData.tournamentId}
                    onChange={(e) => setFormData({ ...formData, tournamentId: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="">Выберите турнир (необязательно)</option>
                  {orderedTournamentStatuses.map((status) => (
                    <optgroup
                      key={status}
                      label={`──────── ${TOURNAMENT_STATUS_LABELS[status] || status} ────────`}
                    >
                      {(groupedTournaments[status] || []).map((tournament) => (
                        <option key={tournament.id} value={tournament.id}>
                          {tournament.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Статус</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'DRAFT' | 'POSTED' | 'CANCELLED' })}
                    className="mt-1 block w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="DRAFT">Черновик</option>
                    <option value="POSTED">Проведен</option>
                    <option value="CANCELLED">Отменен</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                  >
                    {editingExpense ? 'Обновить' : 'Создать'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для просмотра JSON */}
      {showJsonModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-4 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-2">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-base font-medium text-gray-900">
                  JSON данные документа "Расход денежных средств"
                </h3>
                <button
                  onClick={handleCloseJsonModal}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                  title="Закрыть"
                >
                  ×
                </button>
              </div>
              <div className="bg-gray-100 rounded p-4 max-h-96 overflow-auto">
                <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                  {selectedExpenseJson}
                </pre>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleCloseJsonModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashExpenses;
